import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useApi } from "@/core/api";
import { normalizePhoneWithPlusPrefix } from "@/core/utils/phone";
import { ICustomer } from "@/customer/customer";
import { invalidateCustomerQueries } from "@/customer/query";
import { getEnrichedCustomer } from "@/customer/utils";
import { REFERRAL_STATUS } from "./constants";
import type {
  IReferral,
  ReferralColumn,
  ReferralUpdateParams,
} from "./referral";
import { getEnrichedReferral, groupReferralsByColumnStatuses } from "./utils";

export const referralsKey = (businessId?: string | null) =>
  ["referrals", businessId] as const;

export const useGetReferrals = <TData = IReferral[]>(
  businessId?: string | null,
  select?: (data: IReferral[]) => TData,
) => {
  const api = useApi();

  const queryParams = new URLSearchParams();
  if (businessId) {
    queryParams.set("business_id", businessId);
  }

  return useQuery({
    queryKey: referralsKey(businessId),
    queryFn: () =>
      api
        .get("/referrals", { params: queryParams })
        .then((response) => response.data.map(getEnrichedReferral)),
    select,
    enabled: !!businessId,
  });
};

export const useGetReferral = (
  businessId?: string | null,
  referralId?: string | null,
) =>
  useGetReferrals(
    businessId,
    useCallback(
      (referrals: IReferral[]) =>
        referrals.find((referral) => referral.id === referralId),
      [referralId],
    ),
  );

export const useGetActiveReferrals = (businessId?: string | null) =>
  useGetReferrals(businessId, (referrals) =>
    referrals.filter(
      (referral) =>
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        ![REFERRAL_STATUS.PAID, REFERRAL_STATUS.LOST].includes(referral.status),
    ),
  );

export const useGetReferralsByColumn = (
  column: ReferralColumn,
  businessId?: string | null,
) =>
  useGetReferrals(
    businessId,
    useCallback(
      (referrals: IReferral[]) =>
        groupReferralsByColumnStatuses(referrals)[column],
      [column],
    ),
  );

export const useGetRepAssignedReferrals = (
  repAssignedId: string,
  businessId?: string,
) =>
  useGetReferrals(
    businessId,
    useCallback(
      (referrals: IReferral[]) =>
        referrals.filter((referral) => {
          if (["PAID", "LOST"].includes(referral.status)) {
            return false;
          }
          return (
            referral.assigned_reps?.some((rep) => rep.id === repAssignedId) ||
            false
          );
        }),
      [repAssignedId],
    ),
  );

export const useGetAdvocateReferrals = (
  advocateId: string,
  businessId?: string | null,
) =>
  useGetReferrals(
    businessId,
    useCallback(
      (referrals: IReferral[]) =>
        referrals.filter((referral) => referral.advocate?.id === advocateId),
      [advocateId],
    ),
  );

export const useUpdateReferral = (businessId?: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<ReferralUpdateParams>;
    }) => {
      const data = await api
        .patch(`/referrals/${id}`, updates)
        .then((response) => response.data)
        .then(getEnrichedReferral);

      queryClient.setQueryData(
        referralsKey(businessId),
        (old: IReferral[] = []) =>
          old.map((referral) => (referral.id === data.id ? data : referral)),
      );

      return data;
    },
    onMutate: async ({ id, updates }) => {
      await queryClient.cancelQueries({ queryKey: referralsKey(businessId) });
      const previousReferrals = queryClient.getQueryData<IReferral[]>(
        referralsKey(businessId),
      );

      queryClient.setQueryData<IReferral[]>(
        referralsKey(businessId),
        (old = []) =>
          old.map((referral) =>
            referral.id === id ? { ...referral, ...updates } : referral,
          ),
      );

      return { previousReferrals };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousReferrals) {
        queryClient.setQueryData<IReferral[]>(
          referralsKey(businessId),
          context.previousReferrals,
        );
      }
    },
  });
};

export const useDeleteReferral = (businessId?: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await api.delete(`/referrals/${id}`);
    },
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: referralsKey(businessId) });
      const previousReferrals = queryClient.getQueryData<IReferral[]>(
        referralsKey(businessId),
      );

      queryClient.setQueryData<IReferral[]>(
        referralsKey(businessId),
        (old = []) => old.filter((referral) => referral.id !== id),
      );

      return { previousReferrals };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousReferrals) {
        queryClient.setQueryData<IReferral[]>(
          referralsKey(businessId),
          context.previousReferrals,
        );
      }
    },
  });
};

interface CreateReferralParams {
  service_needed: string;
  advocate_id?: string;
  advocate_name?: string;
  advocate_phone?: string;
  signup_source_id?: string;
  signup_source_type?: string;
  customer_name: string;
  customer_phone: string;
  business_id: string;
  onboard?: boolean;
}

export const useCreateReferral = (businessId?: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      customer_name,
      customer_phone,
      advocate_id,
      advocate_name,
      advocate_phone,
      onboard,
      signup_source_id,
      signup_source_type,
      ...rest
    }: CreateReferralParams) => {
      const preparedCustomerPhone =
        normalizePhoneWithPlusPrefix(customer_phone);
      const preparedAdvocatePhone = advocate_phone
        ? normalizePhoneWithPlusPrefix(advocate_phone)
        : undefined;

      return api
        .post("/referrals", {
          ...rest,
          potential_customer: {
            name: customer_name,
            phone_number: preparedCustomerPhone,
          },
          advocate: !advocate_id
            ? {
                name: advocate_name,
                phone_number: preparedAdvocatePhone,
                onboard,
                signup_source_id,
                signup_source_type,
              }
            : undefined,
          advocate_id,
        })
        .then((response) => response.data)
        .then(getEnrichedReferral);
    },
    onSuccess: (referral) => {
      // Update all relevant query caches
      const queryKeys = [
        ["customers", businessId, false], // without payouts
        ["customers", businessId, true], // with payouts
      ];

      // Add new customer
      const newCustomer = getEnrichedCustomer(
        referral.potential_customer as ICustomer,
      );

      queryKeys.forEach((queryKey) => {
        queryClient.setQueryData<ICustomer[]>(queryKey, (oldData) => {
          if (!oldData) return oldData;

          // Add empty payouts array if this is the withPayouts query
          if (queryKey[2]) {
            return [
              ...oldData,
              {
                ...(newCustomer as ICustomer),
                payouts: [],
                lead_referral: referral,
              },
            ];
          }
          return [
            ...oldData,
            { ...(newCustomer as ICustomer), lead_referral: referral },
          ];
        });
      });
      // Invalidate and refetch referrals after a successful creation
      queryClient.setQueryData(
        referralsKey(businessId),
        (old: IReferral[] = []) => [...old, referral],
      );
      invalidateCustomerQueries(queryClient, businessId);
    },
  });
};
